import { JbCart } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { refreshTieredProductDetails } from './refreshTieredProductDetails';
import { getCartApiResponse, splitMismatchedItems, updateCartWithCartApiResponse } from '../cartApi';
import { applyLegacyTransformations, getCartQuantities } from '../cartTransformations';
import { fixInvalidCart } from '../fixInvalidCart';
import { setServiceOfferings } from '../parseServiceOfferings';
import { ShopifyStorefrontClient } from '../../api/shopify/ShopifyStorefrontClient';
import { productClient } from '../../api/algolia/ProductClient';
import { getSession, SessionStorageKeys, setSession } from '../sessionStorage';
import { AlgoliaProductHit } from '../types';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { getLocal, LocalStorageKeys } from '../localStorage';

export const CART_UPDATE_EVENT = 'cartUpdate';

const storefrontClient = new ShopifyStorefrontClient();

export const setCartLoading = (isLoading: boolean) => (window.Jbhifi.cartLoading = isLoading);
export const getCartLoading = () => window.Jbhifi.cartLoading;

// To prevent race conditions and allow the cart to be updated from outside react
let _currentCartState: JbCart | undefined = undefined;
export const getCartState = () => _currentCartState;
export const updateCartState = (cart: JbCart | undefined) => {
  _currentCartState = cart;
  setSession(SessionStorageKeys.CachedCart, cart, true);
  window.dispatchEvent(new CustomEvent(CART_UPDATE_EVENT, { detail: cart }));
};

export const updateCart = async (cart: JbCart | undefined) => {
  const { validatedCart, cartApiResponse } = await validateCart(cart);
  const updatedCart = validatedCart || cart;
  setServiceOfferings(cartApiResponse, updatedCart);
  updateCartState(updatedCart);
  setCartLoading(false);
  return { cartApiResponse, validatedCart };
};

export const initialiseCart = async () => {
  setCartLoading(true);
  // Store cart bundle suggestions
  const suggestions = window.Jbhifi?.cartSuggestions;
  const allSuggestedSkus =
    suggestions?.flatMap(suggestion => suggestion.suggestedSkus.map(sku => sku.toString())) || [];
  const searchResults = await productClient.getProductsFromSkus(allSuggestedSkus);
  const suggestedProducts = searchResults?.data?.products ?? [];
  suggestions?.forEach(suggestion => {
    suggestion.suggestedHits = suggestedProducts.filter(hit => suggestion.suggestedSkus.includes(Number(hit.sku)));
  });
  window.Jbhifi.cartSuggestions = suggestions;
  let cart = getSession(SessionStorageKeys.CachedCart, true);
  if (!cart) {
    cart = await storefrontClient.getCart();
  }
  await refreshTieredProductDetails(cart);
  updateCartState(cart);
  await updateCart(cart);
};

export const validateCart = async (cart: JbCart | undefined) => {
  if (!cart) return {};

  // Fix invalid cart (assumed to be from existing legacy cart)
  let validatedCart = await fixInvalidCart(cart);

  //CartAPI validation
  let cartApiResponse;
  if (cart?.items.length) {
    cartApiResponse = await getCartApiResponse(cart);
    if (!cartApiResponse) return { newCart: validatedCart };

    // If there are mismatched items, or bonus items, we need to refresh the basket response from CartAPI
    const splitCart = await splitMismatchedItems(cartApiResponse, cart);
    if (splitCart) {
      cartApiResponse = await getCartApiResponse(splitCart);
      if (!cartApiResponse) return { newCart: validatedCart };
      validatedCart = splitCart;
    }

    // Update cart with services/promotions/etc
    validatedCart = await updateCartWithCartApiResponse(cartApiResponse, validatedCart);
  }

  // Apply transformations with changes from CartAPI
  applyLegacyTransformations(validatedCart);

  // Save count of each item in cart for max quantity validation, to prevent having to check cart quantity on every add
  window.Jbhifi.cartQuantities = getCartQuantities(validatedCart);
  validatedCart.isValidated = true;

  // Set preferred store if it has changed
  const preferredStore = getLocal(LocalStorageKeys.MySelectedStore, true);
  if (
    window.Jbhifi.localisationSettings.shouldSaveStoreToShopifyPreferences &&
    preferredStore?.shopifyId &&
    cart?.preferredStore !== preferredStore.shopifyId
  ) {
    await storefrontClient.setPreferredStore(preferredStore.shopifyId);
  }

  return { validatedCart, cartApiResponse };
};

export const transformBonusItemsForCart = (
  bonusProducts: Array<AlgoliaProductHit> | undefined,
  trackingListName: string | undefined
) => {
  return (
    bonusProducts?.map(hit => {
      return {
        variantId: hit.variant_id,
        quantity: 1,
        attributes: filterAndMapAttributes({
          _availabilityCanPreOrder: hit.availability?.canPreOrder,
          _availabilityFulfilment: hit.availability?.fulfilment,
          _maxQty: hit.availability.fulfilment === 'Digital' ? 1 : hit.product?.limitPerOrder,
          _priceAtc: Math.round(hit.price * 100),
          _itemListName: trackingListName || undefined,
          _reviewCount: hit.product?.numberOfProductReviews,
          _reviewScore: hit.product?.averageProductReviewRating,
        }),
      };
    }) || []
  );
};
